import React from 'react'
import { AmplifyUser } from '@aws-amplify/ui'
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react'
import { UseAuthenticator } from '@aws-amplify/ui-react'
import { Amplify } from 'aws-amplify'
import { Col, Row } from 'react-bootstrap'
import { BrowserRouter as Router, Route, Navigate, useLocation, Routes } from 'react-router-dom'

import type { AppState, AppStateAction } from './App.d'
import './App.css'
import '@aws-amplify/ui-react/styles.css'

import ScreensAccountSettings from './screens/accountSettings/accountSettings'
import ScreensHomeNoPage from './screens/home/nopage'
import ScreensDashboard from './screens/dashboard/dashboardWrapper'
import ScreensInvite from './screens/login/accountInvite'
import ScreensLogin from './screens/login/login'
import ScreensSignup from './screens/login/signup'
import ScreensUsers from './screens/users/users'
import ScreensUsersSingle from './screens/users/singleUser'
import ScreensXeroCallback from './screens/xero/callback'
import ScreensXeroConnections from './screens/xero/connections'
import ScreensXeroSsoRedirect from './screens/xero/redirectSignIn'
import ScreensXeroSubscriptions from './screens/xero/subscriptions'

import ScreensWIP from './screens/wip/wip'
import ScreensInvoices from './screens/invoice/invoices'
import ScreensNewInvoice from './screens/invoice/newInvoice'
import ScreensInvoiceDetails from './screens/invoice/invoiceDetails'
import ScreensInvoiceAllocation from './screens/invoice/invoiceAllocation'
import ScreensInvoicePrepareLineItems from './screens/invoice/invoicePrepareLineItems'
import ScreensReports from './screens/reports/reports'
import ScreensCosts from './screens/costs/costs'

import NoAccess from './components/home/noaccess'
import UserProfile from './components/home/userProfile'

import * as Request from './utilities/request'
import { awsConfig } from './constants/config'
import HeaderNav from './components/home/headerNav'
import Logo from './components/home/logo'
import AccountCreationConfirmation from './screens/login/accountCreation'
import OnBoarding from './screens/onBoarding/onBoarding'

Amplify.configure(awsConfig)

const getUnprotectedRoute = (RouteComponent: () => React.JSX.Element) => <RouteComponent />

const getProtectedRoute = (RouteComponent: () => React.JSX.Element) => {
	const { appState } = React.useContext(AppContext)
	const location = useLocation()
	if (
		location.pathname.endsWith('/callback') &&
		(location.search.includes('?code') || location.search.includes('&code') || location.search.includes('?error') || location.search.includes('&error'))
	) {
		// if we are coming back from xero auth we don't want amplify to take over when it sees ?code
		const newLocationSearch = location.search
			.replace('?code', '?xcode')
			.replace('&code', '&xcode')
			.replace('?error', '?xerror')
			.replace('&error', '?xerror')

		location.search = newLocationSearch
		window.location.replace(`${location.pathname}${newLocationSearch}`)

		return <Navigate to={`${location.pathname}${newLocationSearch}`} replace />
	}
	if (appState.authState.isLoggedIn && appState.accounts.length === 0 && location.pathname !== '/confirmation' && appState.hasNoAccount) {
		return <Navigate to={`/confirmation`} />
	}
	if (appState.authState.isLoggedIn && appState.accounts.length > 0 && location.pathname === '/confirmation') {
		// we are done confirming our choice and created account so log in
		return <Navigate to={`/onboarding`} />
	}
	if (!appState.authState.isLoggedIn) {
		// if we aren't logged in redirect to login page
		const encodedQuery = encodeURIComponent((location ? location.pathname + location.search : '') + location.hash)
		return <Navigate to={`/login?redirect=${encodedQuery}`} />
	}
	if (appState.authState.invitationAccepted === false && location.pathname !== '/denied') {
		// redirect to denied page if the user has declined the invitation
		console.log('denied redirect')
		return <Navigate to={`/denied`} />
	}
	if (appState.authState.invitationAccepted === null && location.pathname !== '/invite') {
		// redirect to acceptance page if we haven't accepted yet
		console.log('invite redirect')
		return <Navigate to={`/invite${location.pathname !== '/invite' ? `?redirect=${location.pathname}` : ''}`} />
	}
	// we are all good to go
	return <RouteComponent />
}

//the route with path '/' to be modified once we decide where we separate leaveCal and oversight
const MyRoutes = () => {
	return (
		<Routes>
			<Route path="/confirmation" element={getProtectedRoute(AccountCreationConfirmation)} />
			<Route path="/login" element={getUnprotectedRoute(ScreensLogin)} />
			<Route path="/signup" element={getUnprotectedRoute(ScreensSignup)} />
			<Route path="/xero/sso" element={getUnprotectedRoute(ScreensXeroSsoRedirect)} />

			<Route path="/onboarding" element={getProtectedRoute(OnBoarding)} />
			<Route path="/:xero/callback" element={getProtectedRoute(ScreensXeroCallback)} />
			<Route path="/xero/connections" element={getProtectedRoute(ScreensXeroConnections)} />
			<Route path="/xero/subscription" element={getProtectedRoute(ScreensXeroSubscriptions)} />
			<Route path="/denied" element={getProtectedRoute(ScreensInvite)} />
			<Route path="/invite" element={getProtectedRoute(ScreensInvite)} />
			<Route path="/users" element={getProtectedRoute(ScreensUsers)} />
			<Route path="/user/:userID" element={getProtectedRoute(ScreensUsersSingle)} />
			<Route path="/accountSettings" element={getProtectedRoute(ScreensAccountSettings)} />

			<Route path="/" element={getProtectedRoute(ScreensDashboard)} />
			<Route path="/wip" element={getProtectedRoute(ScreensWIP)} />
			<Route path="/invoices" element={getProtectedRoute(ScreensInvoices)} />
			<Route path="/invoice/new" element={getProtectedRoute(ScreensNewInvoice)} />
			<Route path="/invoice/:invoiceID" element={getProtectedRoute(ScreensInvoiceDetails)} />
			<Route path="/invoice/:invoiceID/allocation" element={getProtectedRoute(ScreensInvoiceAllocation)} />
			<Route path="/invoice/:invoiceID/narration" element={getProtectedRoute(ScreensInvoicePrepareLineItems)} />
			<Route path="/reports" element={getProtectedRoute(ScreensReports)} />
			<Route path="/costs" element={getProtectedRoute(ScreensCosts)} />
			<Route path="*" element={getUnprotectedRoute(ScreensHomeNoPage)} />
		</Routes>
	)
}

const initialState = {
	authState: {
		isLoggedIn: false,
		apiToken: null,
		attributes: null,
		invitationAccepted: null,
	},
	onBoardingStep: null,
	permission: { roleID: '' },
	accounts: [],
}

const appReducer: React.Reducer<AppState, AppStateAction> = (state, action) => {
	switch (action.state) {
		case 'signedIn':
			return {
				...state,
				accounts: action.data.accounts || [],
				authState: {
					...state.authState,
					isLoggedIn: true,
					apiToken: action.data.token,
					attributes: action.data.attributes,
					invitationAccepted: true,
				},
			}
		case 'signIn':
			return {
				...state,
				authState: {
					...state.authState,
					isLoggedIn: false,
				},
			}
		case 'permission':
			return {
				...state,
				permission: action.data,
				authState: {
					...state.authState,
					invitationAccepted: action.accepted,
				},
			}
		case 'accounts':
			return {
				...state,
				accounts: action.data,
				hasNoAccount: false,
			}
		case 'selectedOrg':
			return {
				...state,
				selectedOrg: action.data,
			}
		case 'noAccounts':
			return {
				...state,
				hasNoAccount: action.data.hasNoAccount,
			}
		case 'newAccountConfirmed':
			return {
				...state,
				permission: action.data.permission,
				accounts: action.data.accounts,
				hasNoAccount: false,
				onBoardingStep: 0,
				authState: {
					...state.authState,
					attributes: action.data.attributes,
				},
			}
		case 'onboarding':
			return {
				...state,
				onBoardingStep: action.data.onBoardingStep,
			}
		default:
			return state
	}
}

interface AppProps {
	auth: {
		signOut?: UseAuthenticator['signOut']
		user?: AmplifyUser
	}
}

const AppContext = React.createContext<{
	appState: AppState
	updateAppState: React.Dispatch<AppStateAction>
	loading: boolean
}>({ appState: initialState, updateAppState: () => {}, loading: true })

// anything that exists over each page goes here
const App = (props: AppProps) => {
	const [appState, setAppState] = React.useReducer<AppState, [AppStateAction]>(appReducer, initialState)
	const [isLoading, updateIsLoading] = React.useState<boolean>(true)

	React.useEffect(() => {
		const userSession = props.auth.user?.getSignInUserSession()
		const attributes = props.auth.user?.attributes
		if (userSession && userSession.getIdToken().getJwtToken()) {
			setAppState({
				state: 'signedIn',
				data: { token: userSession.getIdToken().getJwtToken(), attributes },
			})
		}
	}, [props.auth.user])

	React.useEffect(() => {
		const getUserPermissions = async () => {
			if (appState.authState.attributes && appState.authState.attributes['custom:userId']) {
				const req = appState.hasNoAccount
					? await Request.get(`user/${appState.authState.attributes['custom:userId']}/user`)
					: await Request.get(`user/${appState.authState.attributes['custom:userId']}`)
				if (req.data && req.data.users && req.data.users.length > 0) {
					setAppState({
						state: 'permission',
						data: {
							roleID: req.data.users[0].billyPermission.permissionID || null,
						},
						accepted: req.data.users[0].invitationAccepted,
					})
				}
			}
		}

		const getUserAccounts = async () => {
			const req = await Request.get(`account`)
			if (req.data.accounts.length > 0) {
				setAppState({ state: 'accounts', data: req.data.accounts })
			} else {
				setAppState({ state: 'noAccounts', data: { ...appState, hasNoAccount: true } })
			}
			updateIsLoading(false)
		}

		if (appState.authState.isLoggedIn) {
			getUserPermissions()
			getUserAccounts()
		}
	}, [appState.authState.isLoggedIn, appState.hasNoAccount]) // eslint-disable-line

	React.useEffect(() => {
		if (appState.authState.attributes === undefined) {
			window.location.search = ''
			// window.location.reload()
		}
	}, [appState.authState.attributes])

	return (
		<AppContext.Provider value={{ appState, updateAppState: setAppState, loading: isLoading }}>
			{appState.authState.isLoggedIn && (
				<Row className="p-3 border-bottom align-items-center" style={{ backgroundColor: 'var(--color-navy)' }}>
					<Col sm="auto" className="pr-5">
						<Logo height={'48px'} />
					</Col>
					<Col sm="auto">
						<HeaderNav />
					</Col>
					<Col />
					<Col sm="auto">
						<UserProfile
							authState={appState.authState}
							userAttributes={appState.authState.attributes}
							permission={appState.permission}
							accounts={appState.accounts}
							selectedOrg={appState.selectedOrg}
						/>
					</Col>
				</Row>
			)}
			<Row>
				<Col>
					{!isLoading &&
					appState.authState.isLoggedIn &&
					(appState.accounts.length === 0 || appState.permission.roleID === '') &&
					!appState.hasNoAccount ? (
						<Col style={{ display: 'flex', justifyContent: 'center' }}>
							<NoAccess appState={appState} />
						</Col>
					) : (
						<MyRoutes />
					)}
				</Col>
			</Row>
		</AppContext.Provider>
	)
}

const AppWithAuthContext = () => {
	const { signOut, user } = useAuthenticator((context) => [context.user])

	return <App auth={{ signOut, user }} />
}

const AppWithAuth = () => (
	<Router>
		<Authenticator.Provider>
			<AppWithAuthContext />
		</Authenticator.Provider>
	</Router>
)

export default AppWithAuth
export { AppContext }
